import { useState, useEffect, useCallback } from 'react';
import { Plus, Check, X, Edit2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { api } from '../../../core/apiClient';

interface EstatCrmRow {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  isActive: number;
  usedInEc: number;
  usedInPressupost: number;
  usedInLicitacions: number;
}

function InlineInput({ value, onChange, placeholder, className = '' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`h-7 text-xs px-2 ${className}`}
    />
  );
}

const emptyForm = () => ({ code: '', name: '', usedInEc: false, usedInPressupost: false, usedInLicitacions: false });
type Form = ReturnType<typeof emptyForm>;

const API_PATH = '/admin/estats-crm';

/**
 * Taula CRUD d'"Estats CRM" — catàleg unificat d'estats d'Expedient Comercial,
 * Pressupost i Licitacions (2026-10-19/20). Mateixa UX que
 * SimpleCatalogTable.tsx (llistat + reorder + alta + edició + baixa lògica),
 * pero amb tres checkbox en lloc de "Descripció" per marcar en quin(s)
 * mòdul(s) es fa servir cada estat — s'activa el checkbox corresponent i
 * l'estat apareix automàticament als desplegables/filtres d'aquell mòdul
 * (vegeu ExpedientComercialForm.tsx/List.tsx, BudgetForm.tsx/List.tsx i
 * Licitacions.tsx, que llegeixen /admin/estats-crm?module=ec|pressupost|licitacions).
 */
export default function EstatsCrmTable({ showInactive }: { showInactive: boolean }) {
  const [items, setItems] = useState<EstatCrmRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState<Form>(emptyForm());
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Form>(emptyForm());
  const [dragId, setDragId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: EstatCrmRow[] }>(API_PATH);
      setItems(res.data ?? []);
    } catch {
      setError('Error en carregar les dades');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const saveNew = async () => {
    if (!newForm.code.trim() || !newForm.name.trim()) return;
    try {
      await api.post(API_PATH, newForm);
      setAdding(false);
      setNewForm(emptyForm());
      fetchItems();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en crear el registre');
    }
  };

  const startEdit = (item: EstatCrmRow) => {
    setEditId(item.id);
    setEditForm({ code: item.code, name: item.name, usedInEc: !!item.usedInEc, usedInPressupost: !!item.usedInPressupost, usedInLicitacions: !!item.usedInLicitacions });
  };

  const saveEdit = async () => {
    if (editId === null) return;
    try {
      await api.put(`${API_PATH}/${editId}`, editForm);
      setEditId(null);
      fetchItems();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en desar els canvis');
    }
  };

  const toggleUsage = async (item: EstatCrmRow, field: 'usedInEc' | 'usedInPressupost' | 'usedInLicitacions') => {
    try {
      await api.put(`${API_PATH}/${item.id}`, { [field]: !item[field] });
      fetchItems();
    } catch { setError('Error en canviar l\'ús del mòdul'); }
  };

  const toggleActive = async (item: EstatCrmRow) => {
    try {
      await api.put(`${API_PATH}/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchItems();
    } catch { setError('Error en canviar l\'estat'); }
  };

  const remove = async (item: EstatCrmRow) => {
    try {
      await api.delete(`${API_PATH}/${item.id}`);
      fetchItems();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en eliminar el registre');
    }
  };

  const onDragStart = (id: number) => setDragId(id);
  const onDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setDragOverId(id); };
  const onDragEndReset = () => { setDragId(null); setDragOverId(null); };
  const onDrop = async (targetId: number) => {
    if (!dragId || dragId === targetId) { onDragEndReset(); return; }
    const reordered = [...items];
    const from = reordered.findIndex((it) => it.id === dragId);
    const to = reordered.findIndex((it) => it.id === targetId);
    if (from === -1 || to === -1) { onDragEndReset(); return; }
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setItems(reordered);
    onDragEndReset();
    try { await api.put(`${API_PATH}/reorder`, { ids: reordered.map((it) => it.id) }); }
    catch { setError('Error en desar l\'ordre'); fetchItems(); }
  };

  const visible = showInactive ? items : items.filter((it) => it.isActive);

  const UsageCheckbox = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 cursor-pointer accent-blue-600" />
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
          {error}
          <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="rounded-xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr>
              <th className="px-2 py-2 w-6"></th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Codi</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
              <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-36">Expedients Comercials</th>
              <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-28">Pressupostos</th>
              <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-28">Licitacions</th>
              <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
              <th className="px-4 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
            ) : visible.length === 0 && !adding ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
            ) : (
              visible.map((item) => (
                <tr
                  key={item.id}
                  draggable={editId !== item.id}
                  onDragStart={() => onDragStart(item.id)}
                  onDragOver={(e) => onDragOver(e, item.id)}
                  onDrop={() => onDrop(item.id)}
                  onDragEnd={onDragEndReset}
                  className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${dragOverId === item.id && dragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                >
                  {editId === item.id ? (
                    <>
                      <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                      <td className="px-4 py-2"><InlineInput value={editForm.code} onChange={(v) => setEditForm((f) => ({ ...f, code: v }))} placeholder="CODI" /></td>
                      <td className="px-4 py-2"><InlineInput value={editForm.name} onChange={(v) => setEditForm((f) => ({ ...f, name: v }))} placeholder="Nom" /></td>
                      <td className="px-4 py-2 text-center"><UsageCheckbox checked={editForm.usedInEc} onChange={() => setEditForm((f) => ({ ...f, usedInEc: !f.usedInEc }))} /></td>
                      <td className="px-4 py-2 text-center"><UsageCheckbox checked={editForm.usedInPressupost} onChange={() => setEditForm((f) => ({ ...f, usedInPressupost: !f.usedInPressupost }))} /></td>
                      <td className="px-4 py-2 text-center"><UsageCheckbox checked={editForm.usedInLicitacions} onChange={() => setEditForm((f) => ({ ...f, usedInLicitacions: !f.usedInLicitacions }))} /></td>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1 justify-end">
                          <button onClick={saveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                          <button onClick={() => setEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                      <td className="px-4 py-2 font-mono font-medium">{item.code}</td>
                      <td className="px-4 py-2 font-medium">{item.name}</td>
                      <td className="px-4 py-2 text-center"><UsageCheckbox checked={!!item.usedInEc} onChange={() => toggleUsage(item, 'usedInEc')} /></td>
                      <td className="px-4 py-2 text-center"><UsageCheckbox checked={!!item.usedInPressupost} onChange={() => toggleUsage(item, 'usedInPressupost')} /></td>
                      <td className="px-4 py-2 text-center"><UsageCheckbox checked={!!item.usedInLicitacions} onChange={() => toggleUsage(item, 'usedInLicitacions')} /></td>
                      <td className="px-4 py-2 text-center">
                        <button onClick={() => toggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                          {item.isActive
                            ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                            : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => startEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                          <button onClick={() => remove(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
            {adding && (
              <tr className="border-b bg-blue-50/40">
                <td className="px-2 py-2"></td>
                <td className="px-4 py-2"><InlineInput value={newForm.code} onChange={(v) => setNewForm((f) => ({ ...f, code: v }))} placeholder="CODI" /></td>
                <td className="px-4 py-2"><InlineInput value={newForm.name} onChange={(v) => setNewForm((f) => ({ ...f, name: v }))} placeholder="Nom" /></td>
                <td className="px-4 py-2 text-center"><UsageCheckbox checked={newForm.usedInEc} onChange={() => setNewForm((f) => ({ ...f, usedInEc: !f.usedInEc }))} /></td>
                <td className="px-4 py-2 text-center"><UsageCheckbox checked={newForm.usedInPressupost} onChange={() => setNewForm((f) => ({ ...f, usedInPressupost: !f.usedInPressupost }))} /></td>
                <td className="px-4 py-2 text-center"><UsageCheckbox checked={newForm.usedInLicitacions} onChange={() => setNewForm((f) => ({ ...f, usedInLicitacions: !f.usedInLicitacions }))} /></td>
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2">
                  <div className="flex gap-1 justify-end">
                    <button onClick={saveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                    <button onClick={() => { setAdding(false); setNewForm(emptyForm()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!adding && (
        <Button size="sm" variant="outline" onClick={() => { setAdding(true); setEditId(null); }}>
          <Plus className="h-4 w-4 mr-1" />
          Nou estat
        </Button>
      )}
    </div>
  );
}
